import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin, Globe, Instagram, Facebook, Mail, Calendar, ArrowRight, Wine, Building2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { BACKEND_URL } from '../lib/api';

/**
 * Public organiser profile — /organiser/:slug.
 * Shows non-sensitive profile fields + grid of upcoming active events.
 * Never exposes banking, phone, payouts, drafts, or cancelled events
 * (the backend strips those before responding).
 */

const formatDate = (iso) => {
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-ZA', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const setSeo = ({ title, description, url }) => {
  document.title = title;
  const ensure = (selector, attrs) => {
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement(selector.includes('meta') ? 'meta' : 'link');
      document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    return el;
  };
  ensure('meta[name="description"]', { name: 'description', content: description });
  ensure('meta[property="og:title"]', { property: 'og:title', content: title });
  ensure('meta[property="og:description"]', { property: 'og:description', content: description });
  ensure('meta[property="og:type"]', { property: 'og:type', content: 'profile' });
  if (url) ensure('meta[property="og:url"]', { property: 'og:url', content: url });
  ensure('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
};

const SocialLink = ({ href, icon: Icon, label, ...rest }) => {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[#E5E9E6] text-[#1F3D2B] hover:text-[#C9A27A] hover:border-[#C9A27A]/60 transition-colors"
      title={label}
      aria-label={label}
      {...rest}
    >
      <Icon className="w-4 h-4" />
    </a>
  );
};

const OrganiserPublicPage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    axios
      .get(`${BACKEND_URL}/api/organisers/${slug}`)
      .then((r) => {
        if (!active) return;
        setData(r.data);
        const org = r.data?.organiser;
        if (org) {
          const desc = org.bio
            ? org.bio.slice(0, 155)
            : `Discover wine farm events curated by ${org.organisation_name} on VinePass.`;
          setSeo({
            title: `${org.organisation_name} – VinePass`,
            description: desc,
            url: typeof window !== 'undefined' ? window.location.href : undefined,
          });
        }
      })
      .catch((err) => {
        if (!active) return;
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug]);

  // Open Graph / FB / IG handle helpers — accept either a full URL or "@handle".
  const igHref = (raw) => {
    if (!raw) return null;
    const v = raw.trim().replace(/^@/, '');
    return v.startsWith('http') ? v : `https://instagram.com/${v}`;
  };
  const fbHref = (raw) => {
    if (!raw) return null;
    const v = raw.trim();
    return v.startsWith('http') ? v : `https://facebook.com/${v.replace(/^\/+/, '')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="text-[#4A5D50]">Loading organiser…</p>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] py-20 px-4" data-testid="organiser-not-found">
        <div className="max-w-xl mx-auto text-center">
          <Building2 className="w-12 h-12 text-[#4A5D50]/40 mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-[#112318] mb-2">Organiser not found</h1>
          <p className="text-[#4A5D50] mb-6">
            This profile is either private or no longer available.
          </p>
          <Link to="/events">
            <Button className="rounded-full bg-[#1F3D2B] hover:bg-[#172E20] text-white px-6 h-11">
              Browse all events <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const org = data.organiser;
  const events = data.events || [];

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="organiser-public-page">
      {/* Cover banner */}
      <div
        className="relative h-48 sm:h-64 bg-gradient-to-br from-[#1F3D2B] to-[#264D36]"
        style={
          org.cover_image_url
            ? { backgroundImage: `url(${org.cover_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : {}
        }
        data-testid="organiser-cover"
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 sm:-mt-20 pb-16 relative">
        {/* Profile card */}
        <div className="bg-white border border-[#E5E9E6] rounded-2xl shadow-sm p-6 sm:p-8 mb-10">
          <div className="flex flex-col sm:flex-row sm:items-end gap-5 sm:gap-6">
            <div
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-[#F4F6F3] border border-[#E5E9E6] flex items-center justify-center overflow-hidden shrink-0 -mt-16 sm:-mt-20 shadow-sm"
              data-testid="organiser-logo"
            >
              {org.logo_url ? (
                <img src={org.logo_url} alt={`${org.organisation_name} logo`} className="w-full h-full object-cover" />
              ) : (
                <Wine className="w-10 h-10 text-[#1F3D2B]/50" strokeWidth={1.5} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A27A] mb-1">Organiser</p>
              <h1
                className="text-3xl sm:text-4xl font-semibold text-[#112318] leading-tight"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="organiser-name"
              >
                {org.organisation_name}
              </h1>
              {org.region && (
                <p className="flex items-center gap-1.5 text-sm text-[#4A5D50] mt-1.5">
                  <MapPin className="w-3.5 h-3.5" /> {org.region}
                </p>
              )}
            </div>
            {/* Socials */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="organiser-socials">
              <SocialLink href={org.website} icon={Globe} label="Website" data-testid="organiser-website-link" />
              <SocialLink href={igHref(org.instagram)} icon={Instagram} label="Instagram" data-testid="organiser-instagram-link" />
              <SocialLink href={fbHref(org.facebook)} icon={Facebook} label="Facebook" data-testid="organiser-facebook-link" />
              {org.contact_email && (
                <SocialLink href={`mailto:${org.contact_email}`} icon={Mail} label="Email" data-testid="organiser-email-link" />
              )}
            </div>
          </div>

          {org.bio && (
            <p className="mt-6 text-[#4A5D50] leading-relaxed text-sm sm:text-base whitespace-pre-line" data-testid="organiser-bio">
              {org.bio}
            </p>
          )}
        </div>

        {/* Events grid */}
        <div className="flex items-center justify-between mb-5">
          <h2
            className="text-2xl sm:text-3xl font-semibold text-[#112318]"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="organiser-events-heading"
          >
            Events by {org.organisation_name}
          </h2>
          <p className="text-xs text-[#4A5D50]" data-testid="organiser-event-count">
            {events.length} upcoming
          </p>
        </div>

        {events.length === 0 ? (
          <div
            className="bg-white border border-dashed border-[#E5E9E6] rounded-2xl p-12 text-center"
            data-testid="organiser-events-empty"
          >
            <Calendar className="w-10 h-10 text-[#4A5D50]/40 mx-auto mb-3" />
            <p className="text-[#4A5D50]">
              {org.organisation_name} has no upcoming public events right now. Check back soon.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="organiser-events-grid">
            {events.map((e) => (
              <Link
                key={e.event_id}
                to={`/events/${e.event_id}`}
                className="group bg-white border border-[#E5E9E6] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                data-testid={`organiser-event-card-${e.event_id}`}
              >
                <div className="h-44 overflow-hidden bg-[#F4F6F3]">
                  {e.image_url && (
                    <img
                      src={e.image_url}
                      alt={e.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  )}
                </div>
                <div className="p-5">
                  {e.category && (
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#C9A27A] mb-1.5">{e.category}</p>
                  )}
                  <h3
                    className="text-lg font-semibold text-[#112318] mb-2 line-clamp-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {e.title}
                  </h3>
                  <p className="flex items-center gap-1.5 text-xs text-[#4A5D50] mb-1">
                    <Calendar className="w-3.5 h-3.5" /> {formatDate(e.date)}
                    {e.time && <span className="text-[#4A5D50]/70">· {e.time}</span>}
                  </p>
                  <p className="flex items-center gap-1.5 text-xs text-[#4A5D50] truncate">
                    <MapPin className="w-3.5 h-3.5 shrink-0" /> {e.location}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganiserPublicPage;
